import type { ReactElement } from 'react';

import type { ApiDisplayPlanName, DisplayPlanName } from './plan-display';

import { CheckIcon, CrossIcon } from '@/icons';

type PlanFeature = {
  icon: string | ReactElement;
  /**
   * Leading fragment rendered bold before `text`, e.g. `emphasis: '30 sec.'`
   * with `text: 'monitoring interval'` → "**30 sec.** monitoring interval".
   */
  emphasis?: string;
  text: string;
};

type PlanFeatures = PlanFeature[];

export const planDescription: Record<DisplayPlanName, string> = {
  Free: 'Good for hobby and non-profit projects. No credit card required!',
  Solo: 'Good for freelancers and small sites.',
  Team: 'For small teams running production services.',
  Scale: 'For high-volume, self-serve monitoring.',
  Enterprise: 'For custom scale, compliance & procurement.',
};

/**
 * The plan each card is an upgrade on, rendered as "Everything in {plan}, plus"
 * above the feature list. Free has no predecessor, so it renders no such line and
 * keeps the full list of what it does and does not include.
 */
export const planInheritsFrom: Partial<
  Record<DisplayPlanName, DisplayPlanName>
> = {
  Solo: 'Free',
  Team: 'Solo',
  Scale: 'Team',
  Enterprise: 'Scale',
};

const checkIcon = <CheckIcon className="h-3.5 w-3.5" />;
const crossIcon = <CrossIcon className="h-3.5 w-3.5" />;

/**
 * Free keeps an exhaustive list where the paid plans get a short delta: it is the
 * only card that has to sell the absence of things, and the wide block below the
 * card row gives it four columns to do that in.
 */
const freePlanFeatures: PlanFeatures = [
  { icon: '😐', text: '5 min. monitoring interval' },
  { icon: checkIcon, text: 'HTTP, port & ping monitor' },
  { icon: checkIcon, text: 'Keyword monitor' },
  { icon: crossIcon, text: 'API monitoring' },
  { icon: crossIcon, text: 'UDP monitoring' },
  { icon: crossIcon, text: 'Multi-location monitoring' },
  { icon: crossIcon, text: 'Slow response alerts' },
  { icon: crossIcon, text: 'SSL monitor' },
  { icon: crossIcon, text: 'Domain exp. monitor' },
  { icon: '😐', text: 'Only 5 integrations' },
  { icon: crossIcon, text: 'Full-featured status pages' },
  { icon: crossIcon, text: 'Notify only seats unavailable' },
  { icon: crossIcon, text: 'No login seats available' },
];

/**
 * Paid plans list only what the previous tier does not have, under an
 * "Everything in {plan}, plus" line. The exhaustive matrix lives in the feature
 * comparison table further down the page.
 */
export const planContent: Record<DisplayPlanName, PlanFeatures> = {
  Free: freePlanFeatures,
  Solo: [
    { icon: checkIcon, text: '60-second monitoring interval' },
    { icon: checkIcon, text: 'All monitor types + SSL & DNS' },
    { icon: checkIcon, text: '3 status pages, own domain' },
    { icon: checkIcon, text: '12-month data retention' },
  ],
  Team: [
    { icon: checkIcon, emphasis: '30 sec.', text: 'monitoring interval' },
    { icon: checkIcon, text: '3 seats included' },
    { icon: checkIcon, text: '100 status pages, white-label' },
    { icon: checkIcon, text: 'Webhook, Zapier & PagerDuty' },
  ],
  Scale: [
    { icon: checkIcon, emphasis: '15 sec.', text: 'monitoring interval' },
    { icon: checkIcon, text: '200 or 500 monitors' },
    { icon: checkIcon, text: '5 seats included' },
    { icon: checkIcon, text: 'Unlimited status pages' },
  ],
  Enterprise: [
    {
      icon: checkIcon,
      emphasis: 'Custom & faster',
      text: 'monitoring intervals',
    },
    { icon: checkIcon, text: '500+ monitors' },
    { icon: checkIcon, text: 'Dedicated CSM + private Slack' },
    { icon: checkIcon, text: 'SOC 2, DPA, MSA & security review' },
    { icon: checkIcon, text: 'SLA + white-glove onboarding' },
    { icon: checkIcon, text: 'Custom invoicing (PO, wire, net terms)' },
  ],
};

export const mostPopularPackage: DisplayPlanName = 'Team';

/**
 * Enterprise has no monitor range: it is priced per engagement and picks up where
 * Scale's 500 ceiling stops, so it shows a headline instead of a numeric range.
 */
export const ENTERPRISE_MONITORS_LABEL = 'Any amount you need';

/** Price stand-in on the contact-sales card, where there is no number to show. */
export const ENTERPRISE_PRICE_LABEL = "Let's talk";

export const ENTERPRISE_CTA_LABEL = 'Book a demo';

export const PLAN_MAX_MONITORS: Record<ApiDisplayPlanName, number> = {
  Free: 50,
  Solo: 50,
  Team: 100,
  // Scale tops out at 500; above that is the Enterprise card's job.
  Scale: 500,
};

/**
 * Dependency monitors bundled with each plan. These cards quote a range rather
 * than a selected tier, so the allowance is a range too — Solo spans 1-5 the way
 * its monitors span 10-50.
 *
 * The allowance does not follow from the monitor count (10→1 and 50→5 are a
 * tenth, 100→20 upward a fifth), so both ends are stated rather than derived.
 * Free gets none by design; Enterprise is absent pending a product call on what
 * a custom plan should advertise.
 */
export const PLAN_MIN_DEPENDENCY_MONITORS: Partial<
  Record<DisplayPlanName, number>
> = {
  Solo: 1,
  Team: 20,
  Scale: 40,
};

export const PLAN_MAX_DEPENDENCY_MONITORS: Partial<
  Record<DisplayPlanName, number>
> = {
  Solo: 5,
  Team: 20,
  Scale: 100,
};

/** Pill beside the monitor range on cards that bundle 3rd party service monitors. */
export const DEPENDENCY_MONITORS_BADGE = 'New!';

export const PLAN_MIN_MONITORS: Record<ApiDisplayPlanName, number> = {
  Free: 50,
  Solo: 10,
  Team: 100,
  Scale: 200,
};
